'use client';

import { ComponentProps, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { CacheKeys, GET_STORES_MAX_DISTANCE_KM, PRODUCT_STORE_MENU_ID } from '@headless-workspace/config';
import { useToasts, useUIContext } from '@headless-workspace/core-ui';
import {
    GlowButton,
    GlowDropDownSearch,
    GlowInlineToast,
    GlowModal,
    GlowSegmentedPicker,
    Icons,
    PropsWithLocale,
    ToasterHandle,
    ToastType,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { FormatHelpers } from '@headless-workspace/utils';
import { mutate } from 'swr';
import { useDebounceCallback } from 'usehooks-ts';
import { CommonDI, UserCookiesRepositoryType } from '../../../../DI';
import {
    ProductType,
    useLocalityDetails,
    useProductDetailContext,
    useSelectedStore,
    useStoreAutocomplete,
    useUserGeolocation,
} from '../../../../client';
import { StoreInfoValue } from '../../../../server';
import { DisplayOption, StoreSliderDisplay } from './StoreSliderDisplay';
import { useStoreInfos } from './useStoreInfos';
import { useStoresDisplay } from './useStoresDisplay';

const SEARCH_INPUT_DEBOUNCE_MS = 500;
const STORES_DELIVERY_ID = 'stores-store';

type StoresDeliveryContainerProps = PropsWithLocale & {
    ctaLabel: string;
};

export const StoresDeliveryContainer: Component<StoresDeliveryContainerProps> = ({ locale, ctaLabel }) => {
    const t = useTranslations();

    const {
        menuModal: { close: closeMenu },
    } = useUIContext();
    const toastRef = useRef<ToasterHandle>(null);
    const [favoriteStoreId, setFavoriteStoreId] = useState<string>('');

    const { productDetails } = useProductDetailContext();
    // The case where the sku is not defined is impossible at this level, as the component is only rendered for products that are not PRODUCT_SET.
    const sku = productDetails.type !== ProductType.PRODUCT_SET ? productDetails.sku : undefined;

    // Retrieve selected stores information
    useSelectedStore({
        productId: sku,
        onSuccess({ favoriteStore, selectedStore }) {
            setHighlightedStore(selectedStore ?? favoriteStore);
            setFavoriteStoreId(favoriteStore?.id ?? '');
        },
    });

    const [selectedDisplayOption, setSelectedDisplayOption] = useState<DisplayOption>(DisplayOption.List);
    const [highlightedStore, setHighlightedStore] = useState<StoreInfoValue | undefined>(undefined);

    const { localityDetails, getLocalityDetails, resetLocalityDetails } = useLocalityDetails();
    const { userPosition, userAddress, getCurrentPosition } = useUserGeolocation();
    const { notifyError } = useToasts();

    const {
        stores,
        status: storeQueryStatus,
        currentTime,
    } = useStoreInfos({
        locale,
        getCurrentPosition,
        localityDetails,
        userPosition,
    });

    const {
        stores: storesToDisplay,
        displayMoreStoresVisibleButton,
        setIsMoreStoresVisible,
        noStoresWithStockAvailable,
    } = useStoresDisplay({
        stores,
        productSku: sku,
        favoriteStoreId,
        highlightedStoreId: highlightedStore?.id,
    });

    // TODO: Replace all this block with Autocomplete component's behavior
    const { localitiesResults, searchLocalities, isLoading: isStoreAutocompleteLoading } = useStoreAutocomplete();

    const pickerOptions = useMemo(
        () =>
            Array.from(
                Object.keys(DisplayOption).map((option) => {
                    const displayOptionValue = DisplayOption[option as keyof typeof DisplayOption];
                    return {
                        id: displayOptionValue,
                        label: t(`Modal.stores.label.pickerOptions.${displayOptionValue}`),
                    };
                }),
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const handleDisplayOptionChange = (option: DisplayOption) => {
        setSelectedDisplayOption(option);
    };

    const handleSearchLocationChange = useDebounceCallback(async (input: string) => {
        await searchLocalities(input);
    }, SEARCH_INPUT_DEBOUNCE_MS);

    const onSearchRightIconSubmit = (setPositionCallback: (input: string) => void) => {
        toastRef.current?.showToaster?.();
        resetLocalityDetails();
        getCurrentPosition(setPositionCallback);
    };

    const storesFetched = storeQueryStatus === 'success';

    const noStoreAvailable = storesFetched && noStoresWithStockAvailable;

    const closeProductInformationModal = async () => {
        if (highlightedStore) {
            await CommonDI.get(UserCookiesRepositoryType).createSelectedStoreCookie(locale, highlightedStore.id);
            void mutate([CacheKeys.ProductDeliveryInfo, sku]);
            closeMenu(PRODUCT_STORE_MENU_ID, true);
        } else {
            notifyError(t('Modal.stores.error.noStoreSelected'));
        }
    };

    return (
        <>
            <GlowModal.Content>
                <GlowDropDownSearch
                    id={STORES_DELIVERY_ID}
                    defaultValue={
                        userAddress
                            ? FormatHelpers.formatAddress(
                                  userAddress.postalCode,
                                  userAddress.locality,
                                  userAddress.country,
                              )
                            : ''
                    }
                    optionsList={localitiesResults ?? []}
                    title={t('Modal.stores.label.zipCodeOrCity')}
                    onSearchChange={handleSearchLocationChange}
                    ariaLabelInput={t('Modal.stores.label.zipCodeOrCity')}
                    helperLabel={t('Modal.stores.label.exampleZipOrCity')}
                    rightSearchInputIcon={LocationIcon}
                    isLoading={isStoreAutocompleteLoading}
                    onSearchRightIconSubmit={onSearchRightIconSubmit}
                    handleOptionClick={(option) => getLocalityDetails(option.value)}
                />
                {storeQueryStatus === 'init' && (
                    <GlowInlineToast
                        hasCloseBtn
                        label={{
                            text: t('Modal.stores.toast.searchForAnAddressOrActivateGeolocation'),
                        }}
                        ref={toastRef}
                        crossIconAccessibilityTitle={t('Common.action.label.close')}
                        toastType={ToastType.Info}
                    />
                )}
                {noStoreAvailable && (
                    <GlowInlineToast
                        hasCloseBtn
                        label={{
                            text: t('Modal.stores.toast.noStoreAvailable', {
                                distanceKm: GET_STORES_MAX_DISTANCE_KM,
                            }),
                        }}
                        crossIconAccessibilityTitle={t('Common.action.label.close')}
                        toastType={ToastType.Error}
                    />
                )}

                <GlowSegmentedPicker
                    options={pickerOptions}
                    value={selectedDisplayOption}
                    onChange={handleDisplayOptionChange}
                />
                <StoreSliderDisplay
                    stores={storesToDisplay}
                    storesRecoveryStatus={storeQueryStatus}
                    currentTime={currentTime ?? ''}
                    displayOption={selectedDisplayOption}
                    favoriteStoreId={favoriteStoreId}
                    setHighlightedStore={setHighlightedStore}
                    highlightedStoreId={highlightedStore?.id}
                    userPosition={userPosition}
                    localityDetails={localityDetails}
                    displayMoreStoresVisibleButton={displayMoreStoresVisibleButton}
                    setIsMoreStoresVisible={setIsMoreStoresVisible}
                />
            </GlowModal.Content>
            <GlowModal.Footer>
                <GlowButton onClick={closeProductInformationModal} label={ctaLabel} width={'full'} />
            </GlowModal.Footer>
        </>
    );
};

const LocationIcon = (props: ComponentProps<typeof Icons.Location>) => {
    const t = useTranslations('Common.accessibility.icons');

    return <Icons.Location {...props} title={t('geoLocation')} />;
};
